import React, { Fragment, useEffect, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureStats,
    FeatureSection,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './ReportsMain.css';

/* ---------- Inline SVG icons (no FontAwesome dependency) ---------- */

const STAR_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2l2.9 6.26L21.5 9.3l-4.75 4.63L17.9 20.5 12 17.27 6.1 20.5l1.15-6.57L2.5 9.3l6.6-1.04L12 2z"/>
    </svg>
);

const SIGNAL_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <rect x="2" y="14" width="4" height="7" rx="1"/>
        <rect x="9" y="9" width="4" height="12" rx="1"/>
        <rect x="16" y="4" width="4" height="17" rx="1"/>
    </svg>
);

const WIFI_ICON = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <line x1="12" y1="20" x2="12.01" y2="20"/>
    </svg>
);

const BATTERY_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="7" width="16" height="10" rx="2"/>
        <line x1="22" y1="11" x2="22" y2="13"/>
        <rect x="4" y="9" width="12" height="6" rx="1" fill="currentColor" stroke="none"/>
    </svg>
);

const ARROW_LEFT_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="19" y1="12" x2="5" y2="12"/>
        <polyline points="12 19 5 12 12 5"/>
    </svg>
);

const CHECK_ICON = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

/* ---------- Phone mockup data ---------- */

const LINE_CHART_PATH = 'M 0 100 L 10 100 L 30 70 L 45 40 L 55 90 L 80 85 L 100 75 L 120 80 L 135 65 L 150 100 L 170 100 L 220 100 L 235 15 L 250 80 L 265 95 L 280 100';
const MONTH_TICKS = ['4', '6', '8', '10', '12', '14', '16', '18', '20', '22', '24'];
const WEEK_DAYS = ['22', '23', '24', '25', '26', '27', '28'];
const WEEKLY_BARS = [0, 85, 34, 13, 0, 0, 0];

/* ---------- Lead by Clients horizontal bar chart ---------- */

const LEAD_AXIS_MAX = 6.2;
const LEAD_AXIS_TICKS = [1, 2, 3, 4, 5, 6];
const LEAD_LEGEND_COLORS = ['#f97316', '#eab308', '#22c55e', '#d946ef', '#06b6d4', '#3b82f6', '#ef4444', '#9ca3af'];

const LEAD_CLIENTS = [
    // { name: 'Sem', value: 0.5, color: '#22c55e' },
    // { name: 'Madiha', value: 0.6, color: '#eab308' },
    { name: 'Ava Moreno', value: 1.3, color: '#f97316' },
    { name: 'Ethan Sawyer', value: 1.9, color: '#9ca3af' },
    { name: 'Lily Rose', value: 2.9, color: '#ef4444' },
    { name: 'Caleb Jenkins', value: 4.3, color: '#6366f1' },
    { name: 'Sophia Patel', value: 1.7, color: '#06b6d4' },
    { name: 'Lucas Brooks', value: 1.7, color: '#3b82f6' },
    { name: 'Isabella Martin', value: 0.5, color: '#d946ef' },
    { name: 'Alexander Kim', value: 3.1, color: '#22c55e' },
    { name: 'Emily Wilson', value: 2.7, color: '#eab308' },
    { name: 'Julian Styles', value: 6.2, color: '#f97316' }
];

function LeadByClientsChart({ p }) {
    return (
        <div className="rpt-hbar-card">
            <div className="rpt-hbar-axis">
                <span className="rpt-hbar-axis-spacer" />
                <div className="rpt-hbar-axis-track">
                    {LEAD_AXIS_TICKS.map((k) => (
                        <span
                            key={k}
                            className="rpt-hbar-axis-label"
                            style={{ left: `${(k / LEAD_AXIS_MAX) * 100}%` }}
                        >
                            {k}k
                        </span>
                    ))}
                </div>
            </div>

            <div className="rpt-hbar-body">
                <div className="rpt-hbar-grid" aria-hidden="true">
                    {LEAD_AXIS_TICKS.map((k) => (
                        <span
                            key={k}
                            className="rpt-hbar-gridline"
                            style={{ left: `${(k / LEAD_AXIS_MAX) * 100}%` }}
                        />
                    ))}
                </div>
                {LEAD_CLIENTS.map((client) => (
                    <div className="rpt-hbar-row" key={client.name}>
                        <span className="rpt-hbar-name">{client.name}</span>
                        <div className="rpt-hbar-track">
                            <div
                                className="rpt-hbar-fill"
                                style={{
                                    width: `${(client.value / LEAD_AXIS_MAX) * 100}%`,
                                    background: client.color
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="rpt-hbar-legend">
                {LEAD_LEGEND_COLORS.map((color, idx) => (
                    <span key={idx} className="rpt-hbar-legend-dot" style={{ background: color }} />
                ))}
                <span className="rpt-hbar-legend-label">{p('phoneRevenueByClients')}</span>
            </div>
        </div>
    );
}

/* ---------- Phone mockup: main Reports screen ---------- */

function ReportsPhone({ p }) {
    return (
        <div className="rpt-phone rpt-float">
            <div className="rpt-status">
                <span>10:55</span>
                <div className="rpt-status-icons">{SIGNAL_ICON}{WIFI_ICON}{BATTERY_ICON}</div>
            </div>

            <div className="rpt-appbar">
                <span className="rpt-appbar-back">{ARROW_LEFT_ICON}</span>
                <h3 className="rpt-appbar-title">{p('phoneTitle')}</h3>
                <span className="rpt-appbar-spacer" />
            </div>

            <div className="rpt-screen rpt-scroll">
                <div className="rpt-label">{p('phoneThisMonth')}</div>

                <div className="rpt-cards">
                    <div className="rpt-card rpt-card-due">
                        <div className="rpt-card-label">{p('phoneDueLabel')}</div>
                        <div className="rpt-card-value">{p('phoneDueValue')}</div>
                        <div className="rpt-card-note">{p('phoneDueNote')}</div>
                    </div>
                    <div className="rpt-card rpt-card-paid">
                        <div className="rpt-card-label">{p('phonePaidLabel')}</div>
                        <div className="rpt-card-value">{p('phonePaidValue')}</div>
                        <div className="rpt-card-note">{p('phonePaidNote')}</div>
                    </div>
                </div>

                <div className="rpt-chart-card">
                    <div className="rpt-chart-yaxis">
                        <span>12k</span><span>9k</span><span>6k</span><span>3k</span><span>0</span>
                    </div>
                    <div className="rpt-chart-plot">
                        <svg viewBox="0 0 280 100" preserveAspectRatio="none" className="rpt-chart-svg" aria-hidden="true">
                            <line x1="0" y1="0" x2="280" y2="0" className="rpt-grid" />
                            <line x1="0" y1="25" x2="280" y2="25" className="rpt-grid" />
                            <line x1="0" y1="50" x2="280" y2="50" className="rpt-grid" />
                            <line x1="0" y1="75" x2="280" y2="75" className="rpt-grid" />
                            <line x1="0" y1="100" x2="280" y2="100" className="rpt-grid-base" />
                            <path d={`${LINE_CHART_PATH} Z`} className="rpt-chart-area" />
                            <path d={LINE_CHART_PATH} className="rpt-chart-line" />
                            <circle cx="30" cy="70" r="3" className="rpt-chart-dot" />
                            <circle cx="45" cy="40" r="3" className="rpt-chart-dot" />
                            <circle cx="235" cy="15" r="3.5" className="rpt-chart-dot" />
                            <circle cx="250" cy="80" r="3" className="rpt-chart-dot" />
                        </svg>
                    </div>
                    <div className="rpt-chart-xaxis">
                        {MONTH_TICKS.map((tick) => <span key={tick}>{tick}</span>)}
                    </div>
                    <div className="rpt-legend">
                        <span className="rpt-dot rpt-dot-purple" />
                        <span>{p('phoneRevenueLegend')}</span>
                    </div>
                </div>

                <div className="rpt-label">{p('phonePerformance')}</div>

                <div className="rpt-perf-card">
                    <div className="rpt-tabs">
                        <span className="rpt-tab rpt-tab-active">{p('phoneWeekly')}</span>
                        <span className="rpt-tab">{p('phoneMonthly')}</span>
                        <span className="rpt-tab">{p('phoneYearly')}</span>
                    </div>
                    <div className="rpt-bars">
                        {WEEKLY_BARS.map((height, idx) => (
                            <div className="rpt-bar-col" key={idx}>
                                <div className="rpt-bar" style={{ height: `${height}%` }} />
                            </div>
                        ))}
                    </div>
                    <div className="rpt-chart-xaxis">
                        {WEEK_DAYS.map((day) => <span key={day}>{day}</span>)}
                    </div>
                    <div className="rpt-legend">
                        <span className="rpt-dot rpt-dot-purple" />
                        <span>{p('phoneWeeklyLegend')}</span>
                    </div>
                </div>

                <div className="rpt-label">{p('phoneLeadByClients')}</div>

                <LeadByClientsChart p={p} />
            </div>
        </div>
    );
}

/* ---------- Phone mockup: performance breakdown ---------- */

function LeadPhone({ p }) {
    return (
        <div className="rpt-phone rpt-phone-sm rpt-float-delayed">
            <div className="rpt-status">
                <span>10:55</span>
                <div className="rpt-status-icons">{SIGNAL_ICON}{WIFI_ICON}{BATTERY_ICON}</div>
            </div>

            <div className="rpt-screen rpt-scroll">
                <div className="rpt-label">{p('phoneLeadByClients')}</div>

                <LeadByClientsChart p={p} />
            </div>
        </div>
    );
}

function ReportsMain(){
    const { t, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.reports.${key}`);

    const [loader, setLoader] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    if (!loader) {
        return <Loader/>;
    }

    const stats = [
        { label: p('statRevenueLabel'), value: formatCurrency(p('statRevenueValue')) },
        { label: p('statPaidLabel'), value: formatCurrency(p('statPaidValue')), tone: 'paid' },
        { label: p('statOutstandingLabel'), value: formatCurrency(p('statOutstandingValue')), tone: 'outstanding' },
        { label: p('statOverdueLabel'), value: formatCurrency(p('statOverdueValue')), tone: 'overdue' }
    ];

    const insightsBullets = [p('insightsBullet1'), p('insightsBullet2'), p('insightsBullet3')];

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.reports} locale={locale} />

            <FeatureHero
                className="rpt-hero"
                eyebrow={<><span className="fc-hero-eyebrow-icon">{STAR_ICON}</span>{p('eyebrow')}</>}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
                image={<ReportsPhone p={p} />}
            />

            {/* Understand Your Numbers */}
            <FeatureSection id="feature-content" className="rpt-numbers-section" title={p('sectionTitle')}>
                <FeatureStats stats={stats} />
            </FeatureSection>

            {/* Make Better Business Decisions */}
            <FeatureSection
                className="rpt-decisions-section"
                title={p('sectionTwoTitle')}
                description={p('sectionTwoDescription')}
            />

            {/* Track Performance & Lead by Clients */}
            <FeatureSection className="rpt-insights-section">
                <div className="rpt-split">
                    <div className="rpt-split-visual">
                        <LeadPhone p={p} />
                    </div>
                    <div className="rpt-split-text">
                        <span className="rpt-eyebrow">{p('insightsEyebrow')}</span>
                        <h2 className="rpt-split-title">{p('insightsTitle')}</h2>
                        <p className="rpt-split-desc">{p('insightsDescription')}</p>
                        <ul className="rpt-bullets">
                            {insightsBullets.map((bullet, idx) => (
                                <li className="rpt-bullet" key={idx}>
                                    <span className="rpt-bullet-icon">{CHECK_ICON}</span>
                                    <span className="rpt-bullet-text">{bullet}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </FeatureSection>

            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
        </Fragment>
    )
}

export default ReportsMain;
